import CheckOutlined from '@ant-design/icons/CheckOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import Button from 'antd/es/button';
import Typography from 'antd/es/typography';
import React from 'react';
import { Link } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';

import type { GetTeamByIdResponse } from '@mai/platform-api/src/routes/teams.schemas';
import type { GetUserByIdResponse } from '@mai/platform-api/src/routes/users.schema';

const { Text, Title } = Typography;

const StyledPopoverContent = styled.div`
  width: 280px;
  max-width: 90vw;
`;

const StyledSection = styled.div`
  padding: ${({ theme }) => theme.paddingSM}px;

  &:not(:last-child) {
    border-bottom: ${({ theme }) =>
      `${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
  }
`;

const StyledActiveTeam = styled.div`
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

const StyledTeamList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const StyledTeamItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.paddingXS}px 0;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;

  &:hover {
    background: ${({ theme }) => theme.colorFillTertiary};
  }
`;

const StyledUserActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXXS}px;
`;

const StyledUserHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.paddingXS}px
    ${({ theme }) => theme.paddingSM}px;
  border-top: ${({ theme }) =>
    `${theme.lineWidth}px solid ${theme.colorBorderSecondary}`};
`;

const StyledActionLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colorText};
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  padding: ${({ theme }) => theme.paddingXXS}px 0;
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;
  transition: background-color ${({ theme }) => theme.motionDurationSlow};

  &:hover {
    background: ${({ theme }) => theme.colorFillTertiary};
    color: ${({ theme }) => theme.colorText};
  }
`;

interface TeamSwitcherPopoverProps {
  teams: GetTeamByIdResponse[];
  currentTeamId?: string;
  user?: GetUserByIdResponse;
  userId: string | null;
  isLoading: boolean;
  onTeamSelect: (teamId: string) => void;
  onSettingsClick: (teamId: string) => void;
  onProfileClick: () => void;
  version: string;
  backendVersion?: string;
}

export const TeamSwitcherPopover: React.FC<TeamSwitcherPopoverProps> = ({
  teams,
  currentTeamId,
  user,
  userId,
  isLoading,
  onTeamSelect,
  onSettingsClick,
  onProfileClick,
  version,
  backendVersion,
}) => {
  const theme = useTheme();

  const currentTeam = teams.find((team) => team.teamId === currentTeamId);

  return (
    <StyledPopoverContent>
      {/* Active Team Section */}
      <StyledSection>
        <StyledActiveTeam>
          <Title level={5} style={{ margin: 0, fontSize: theme.fontSize }}>
            {currentTeam?.name || 'No team selected'}
          </Title>
          {currentTeam && (
            <Button
              type="default"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => onSettingsClick(currentTeam.teamId)}
              style={{
                marginTop: theme.marginXXS,
                fontSize: theme.fontSizeSM,
              }}
            >
              Settings
            </Button>
          )}
        </StyledActiveTeam>
      </StyledSection>

      {/* Teams Section */}
      <StyledSection>
        <StyledTeamList>
          {isLoading ? (
            <Text>Loading teams...</Text>
          ) : teams.length === 0 ? (
            <Text type="secondary">No teams available</Text>
          ) : (
            teams.map((team) => (
              <StyledTeamItem
                key={team.teamId}
                onClick={() => onTeamSelect(team.teamId)}
              >
                <Text style={{ fontSize: theme.fontSize }}>{team.name}</Text>
                {team.teamId === currentTeamId && (
                  <CheckOutlined
                    style={{
                      fontSize: theme.fontSize,
                      color: theme.colorPrimary,
                    }}
                  />
                )}
              </StyledTeamItem>
            ))
          )}
        </StyledTeamList>
      </StyledSection>

      {/* User Profile Section */}
      <StyledSection>
        <StyledUserHeader>
          <div>
            <Text strong style={{ fontSize: theme.fontSize, display: 'block' }}>
              {user?.fullName || 'User'}
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: '10px', display: 'block' }}
            >
              {user?.userId || userId || 'Loading...'}
            </Text>
          </div>
        </StyledUserHeader>

        <StyledUserActions>
          <StyledActionLink to="/profile" onClick={onProfileClick}>Profile</StyledActionLink>
          <StyledActionLink to="/auth/logout">Logout</StyledActionLink>
        </StyledUserActions>
      </StyledSection>

      {/* Footer */}
      <StyledFooter>
        <div>
          <Text style={{ fontSize: '10px', fontFamily: 'monospace' }}>
            UI: {version.substring(0, 7)}
          </Text>
          {backendVersion && (
            <Text
              style={{
                fontSize: '10px',
                fontFamily: 'monospace',
                marginLeft: theme.marginXS,
              }}
            >
              API: {backendVersion.substring(0, 7)}
            </Text>
          )}
        </div>
      </StyledFooter>
    </StyledPopoverContent>
  );
};
